/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

// Material UI
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Button from '@material-ui/core/Button';
import Divider from '@material-ui/core/Divider';
import { withStyles } from '@material-ui/core/styles';

// Checkout UI
import GetListOfHolidays from './GetListOfHolidays';

// Helper
import * as CalendarUtility from '../CalendarView/helpers/calendarUtility';
import { trackEvent } from '../../../../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';

const styles = (theme) => ({
    shippingDetails: {
        margin: '18px 0px',
    },
    legends: {
        backgroundColor: theme.palette.grey1,
        padding: '0px',
    },
    sidePadding: {
        padding: '5px',
        boxSizing: 'border-box',
    },
    applyButton: {
        backgroundColor: theme.palette.buttonBackgroundColor,
        color: theme.palette.primary?.contrastText,
        marginTop: '10px',
    },
    applyCancelButton: {
        color: theme.palette.primary?.main,
        marginRight: '10px !important',
        border: `1px solid ${theme.palette.primary?.main}`,
    },
    forceExactDay: {
        marginTop: '-14px',
    },
    arrivalDate: {
        paddingBottom: '5px',
    },
    orDivider: {
        width: '100%',
        fontStyle: 'italic',
        display: 'flex',
        '& span:first-of-type, & span:last-of-type': {
            flex: '1',
            borderBottom: '1px solid #ae9999',
            maxHeight: '9px',
        },
        '& span:not(:first-of-type):not(:last-of-type)': {
            padding: '0 10px',
            fontSize: '12px',
        },
    },
    applyButtonForRaised: {
        position: 'fixed',
        bottom: '0',
        left: '0',
        right: '0',
        width: '100%',
        zIndex: '9',
        margin: '12px',
        maxWidth: 'calc(100% - 24px)',
        borderRadius: '20px',
        lineHeight: '1.46em',
        fontSize: '1em',
        boxShadow: 'none',
        textTransform: 'none',
        fontWeight: '700',
        '&:hover': {
            boxShadow: 'none',
            backgroundColor: theme.palette.buttonBackgroundColorSecondary,
            color: theme.palette.primary?.contrastText,
        },
    },
    exactDay: {
        marginTop: '15px',
    },
    // visuallyHidden class for elements we need to stay for screen readers, like input labels
    visuallyHidden: {
        border: 0,
        clip: 'rect(0 0 0 0)',
        height: 1,
        margin: -1,
        overflow: 'hidden',
        padding: 0,
        position: 'absolute',
        width: 1,
    },
    [theme.breakpoints.up('sm')]: {
        applyButtonForRaised: {
            margin: '0px',
            position: 'relative',
            fontSize: '1em',
            maxWidth: '100%',
            borderRadius: '0px',
            lineHeight: '1.86em',
        },
        sidePadding: {
            padding: '0px 5px',
        },
        arrivalDate: {
            backgroundColor: (theme.palette.primary?.[200] || '#d5d5d5'),
            padding: '10px',
            '& h6': {
                '&:first-child': {
                    fontSize: '17px',
                    fontWeight: '600',
                },
                fontSize: '1em',
            },
        },
        orDivider: {
            margin: '10px 0px',
        },
        buttonContainer: {
            margin: '10px 0',
            width: '100%',
            display: 'flex',
        },
        exactDaySection: {
            border: `1px solid ${(theme.palette.primary?.[200] || '#d5d5d5')}`,
            padding: '11px 11px 18px 11px',
            '& hr': {
                display: 'none',
            },
            '& $exactDay': {
                margin: '0px',
                fontSize: '1em',
                fontWeight: 600,
            },
            '& $forceExactDay': {
                margin: '0px',
                '& span': {
                    fontSize: '1em',
                },
            },
        },
        monthlyWeeklyAlign: {
            width: '50%',
            float: 'right',
            marginBottom: '30px',
            paddingRight: '15px',
            '& $buttonContainer': {
                position: 'relative',
            },
            '& $arrivalDate': {
                backgroundColor: 'transparent',
                padding: '0px',
            },
        },
    },
});

const ArrivalDetails = (props) => {
    const {
        arrivalDateDetails,
        forceExactDay,
        shippingDetails,
        classes,
        isPassportUser,
        passportSubscriptionStatus,
        selectedDateAnHoliday,
        shopRunnerLoggedIn,
        calendarResponse,
        isMonthDatedCalendar,
        isWeekDatedCalendar,
        onChangeForceExactDay,
        getCancelButton,
        isUseCalendarDateTypeByEnabled,
    } = props;
    const {
        calendarDateType,
        dateObj,
        deliveryDate,
        rangeStartDate,
        upcharge,
        guaranteedChargeToApply,
        surcharge,
        holidayCode,
        productSku,
    } = arrivalDateDetails;
    let arrivalDateString = '';
    let arrivalDate = '';
    let selectedHolidayCode = '';

    if (selectedDateAnHoliday && holidayCode && shippingDetails[holidayCode] && shippingDetails[holidayCode].length > 0) {
        const holidayDetails = shippingDetails[holidayCode][0];
        selectedHolidayCode = holidayCode;
        arrivalDateString = `Guaranteed by ${holidayDetails.holidayDesc}`;
    } else if (isUseCalendarDateTypeByEnabled && dateObj && calendarDateType === 'by') {
        arrivalDateString = `Arrives by ${dateObj.toDateString()}`;
        arrivalDate = `${dateObj.toDateString()}`;
    } else if (dateObj && (calendarDateType === 'on' || forceExactDay)) {
        arrivalDateString = `Arrives on ${dateObj.toDateString()}`;
        arrivalDate = `${dateObj.toDateString()}`;
    } else if (calendarDateType === 'between') {
        const delStartDate = CalendarUtility.convertToDateObj(rangeStartDate);
        const delEndDate = CalendarUtility.convertToDateObj(deliveryDate);
        arrivalDateString = `Arrives Between ${delStartDate.toDateString()} and ${delEndDate.toDateString()}`;
        arrivalDate = ` ${delStartDate.toDateString()} - ${delEndDate.toDateString()}`;
    }

    const dispatch = useDispatch();
    const totalCharges = (parseFloat(upcharge || 0, 10)) + (parseFloat(surcharge || 0, 10));
    const SelDayIndex = (dateObj) ? dateObj.getDay() : '';
    const DayOfTheWeek = CalendarUtility.DaysOfTheWeekFull[SelDayIndex];
    let standardShippingChargeText = 'Standard Shipping Charges';

    if (shopRunnerLoggedIn) {
        if (totalCharges > 0) {
            standardShippingChargeText = `ShopRunner ${DayOfTheWeek} $${totalCharges.toFixed(2)}`;
        } else {
            standardShippingChargeText = 'ShopRunner Shipping $0.00';
        }
    }

    if (isPassportUser || passportSubscriptionStatus) {
        if (totalCharges > 0) {
            standardShippingChargeText = `Passport ${DayOfTheWeek}`;
        } else {
            standardShippingChargeText = 'Passport Standard Shipping $0.00';
        }
    }

    let ChargesApplied = standardShippingChargeText;
    if (forceExactDay && calendarDateType === 'between') {
        ChargesApplied = `Exact Day Delivery - ${standardShippingChargeText} + $${guaranteedChargeToApply}`;
    } else if (totalCharges > 0 && (!isPassportUser && !passportSubscriptionStatus && !shopRunnerLoggedIn)) {
        ChargesApplied = `Shipping upgrade for delivery on ${DayOfTheWeek} + $${totalCharges.toFixed(2)}`;
    } else if (totalCharges > 0) {
        ChargesApplied = `${standardShippingChargeText} + $${totalCharges.toFixed(2)}`;
    }

    const showForceDelivery = ((calendarDateType === 'between') && (calendarResponse.guaranteedCharge && calendarResponse.guaranteedCharge !== 'null'));
    const showHoliday = (calendarResponse?.shipHolidays?.length > 0);
    const orDivider = (<Typography variant="body2" align="center" className={classes.orDivider}><span /> <span>or Choose a Holiday for Delivery</span> <span /></Typography>);

    return (
        <Grid container className={`${classes.sidePadding} ${(isMonthDatedCalendar || isWeekDatedCalendar) ? classes.monthlyWeeklyAlign : ''}`}>
            {deliveryDate && (
                <Grid item xs={12} className={classes.arrivalDate}>
                    <Typography variant="subtitle2" align="center">
                        { arrivalDateString }
                    </Typography>
                    <Typography variant="subtitle2" align="center">
                        { !isMonthDatedCalendar && !isWeekDatedCalendar && ChargesApplied }
                    </Typography>
                </Grid>
            )}
            {(showForceDelivery || showHoliday) && (
                <Grid className={classes.exactDaySection} item xs={12}>
                    {showForceDelivery && (
                        <>
                            <Divider />
                            <Typography
                                className={classes.exactDay}
                                variant="body2"
                            >
                                Need it to arrive on an exact day?
                            </Typography>
                            <FormControlLabel
                                className={classes.forceExactDay}
                                control={(
                                    <Checkbox
                                        checked={forceExactDay}
                                        value="exactDayDelivery"
                                        color="primary"
                                        onChange={onChangeForceExactDay}
                                    />
                                )}
                                label={`Exact Day Delivery add $${calendarResponse.guaranteedCharge}`}
                            />
                        </>
                    )}
                    {showHoliday
                        && (
                            <>
                                {orDivider}
                                <FormControl fullWidth>
                                    <InputLabel className={classes.visuallyHidden}>Select a holiday</InputLabel>
                                    { /* eslint-disable-next-line react/jsx-props-no-spreading */ }
                                    <GetListOfHolidays selectedHoliday={selectedHolidayCode} {...props} />
                                </FormControl>
                            </>
                        )}
                </Grid>
            )}
            <Grid item xs={12} className={classes.buttonContainer}>
                {(getCancelButton)
                    && (
                        <Button
                            fullWidth
                            // variant="contained"
                            // color="secondary"
                            onClick={
                                () => {
                                    props.closeDeliveryCalModel();
                                }
                            }
                            className={`${classes.applyCancelButton} ${classes.applyButtonForRaised}`}
                            data-test="checkout-delivery-calendar-cancel-date"       // do NOT change or remove data-test attribute
                        >
                            Cancel
                        </Button>
                    )}
                <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    onClick={
                        () => {
                            props.applyDateCallBack();
                            dispatch(trackEvent({
                                eventName: 'feature_interaction',
                                eventCategory: 'Checkout',
                                eventAction: 'Delivery Calendar',
                                eventLabel: `${arrivalDate} | ${productSku}`,
                            }));
                            if (totalCharges > 0) {
                                dispatch(trackEvent({
                                    eventName: 'feature_interaction',
                                    eventCategory: 'Checkout',
                                    eventAction: 'Delivery Calendar',
                                    eventLabel: `Surcharge: $${totalCharges.toFixed(2)} | ${productSku}`,
                                }));
                            }
                        }
                    }
                    className={`${classes.applyButton} ${classes.applyButtonForRaised}`}
                    data-test="checkout-delivery-calendar-apply-date"       // do NOT change or remove data-test attribute
                >
                    Apply Date
                </Button>
            </Grid>
        </Grid>
    );
};

ArrivalDetails.propTypes = {
    arrivalDate: PropTypes.string,
    shippingDetails: PropTypes.object.isRequired,
    getDeliveryResponseFor: PropTypes.func.isRequired,
    calendarResponse: PropTypes.object.isRequired,
    shppingDetails: PropTypes.object,
    arrivalDateDetails: PropTypes.object.isRequired,
    classes: PropTypes.object.isRequired,
    forceExactDay: PropTypes.bool.isRequired,
    onChangeForceExactDay: PropTypes.func.isRequired,
    applyDateCallBack: PropTypes.func.isRequired,
    brand: PropTypes.string.isRequired,
    selectedDateAnHoliday: PropTypes.bool.isRequired,
    isMonthDatedCalendar: PropTypes.bool,
    isWeekDatedCalendar: PropTypes.bool,
    isPassportUser: PropTypes.bool.isRequired,
    passportSubscriptionStatus: PropTypes.bool.isRequired,
    shopRunnerLoggedIn: PropTypes.string,
    getCancelButton: PropTypes.bool,
    closeDeliveryCalModel: PropTypes.func.isRequired,
    isUseCalendarDateTypeByEnabled: PropTypes.bool,
};

ArrivalDetails.defaultProps = {
    arrivalDate: null,
    shppingDetails: null,
    getCancelButton: false,
    shopRunnerLoggedIn: '',
    isWeekDatedCalendar: false,
    isMonthDatedCalendar: false,
    isUseCalendarDateTypeByEnabled: false,
};

export default withStyles(styles)(ArrivalDetails);
